/**
 * The transfers report's unit cost and retail price — bata-platform ADR 6130 (#498 and the retail
 * fields beside it), as the pinned client generates them. DEC-106, `docs/adr/9041` amended; the
 * pattern is `docs/adr/9037`'s.
 *
 * ══ PINNED ═══════════════════════════════════════════════════════════════════
 *
 * `src/api/spec/openapi.json` is bata-platform v0.59.0, whose `TransferReportRow`
 * (`GET /inseason/runs/{run_id}/report/transfers`) carries both sets of fields. So the shapes below
 * are the generated row's own fields, picked under the names the workbook and the tests already
 * import, and `TRANSFER_UNIT_COST` / `TRANSFER_RETAIL_PRICE` read the generated row. Until this
 * re-pin the file held #498's fields typed by hand and `TRANSFER_UNIT_COST = null`; the generated
 * fields matched them name for name and type for type (`unit_cost` decimal text).
 *
 * The platform types its vocabularies as plain strings (no enum), so the codes are listed here and
 * `transfer-why-value.test.ts` holds each list equal to the codes the pinned spec's field
 * descriptions name: a code the platform adds fails there until it has words in the catalogues.
 *
 * Retail: on v0.59.0 every row reads `retail_price_not_landed` — which source is the retail price is
 * an open owner decision (bata-data issue 484). The workbook draws that as «n.d.» with the reason
 * in words; it never substitutes a realised, modelled or zero price.
 */

import type { TransferReportRow } from '@/api/generated';

/** The unit cost's fields, verbatim. `unit_cost` is decimal text, as landed. */
export type TransferUnitCost = Pick<
  TransferReportRow,
  'unit_cost' | 'unit_cost_currency' | 'unit_cost_provenance' | 'unit_cost_valid_from' | 'unit_cost_absent_reason'
>;

/**
 * The retail price's fields, verbatim. `retail_price` is decimal text on the tax basis
 * `retail_price_includes_vat` states; `retail_price_vat_rate` is the MULTIPLIER (1.19 for 19%).
 */
export type TransferRetailPrice = Pick<
  TransferReportRow,
  | 'retail_price' | 'retail_price_currency' | 'retail_price_includes_vat' | 'retail_price_vat_rate'
  | 'retail_price_kind' | 'retail_price_provenance' | 'retail_price_valid_from' | 'retail_price_absent_reason'
>;

// Compile-time: the generated fields are what the workbook was written against. A re-pin that
// renames or retypes one fails `npm run typecheck` here, not in a spreadsheet.
type Equal<A, B> = (<T>() => T extends A ? 1 : 2) extends (<T>() => T extends B ? 1 : 2) ? true : false;
const assertType = <T extends true>(): T | undefined => undefined;
assertType<Equal<TransferUnitCost, {
  readonly unit_cost: string | null;
  readonly unit_cost_currency: string | null;
  readonly unit_cost_provenance: string | null;
  readonly unit_cost_valid_from: string | null;
  readonly unit_cost_absent_reason: string | null;
}>>();
assertType<Equal<TransferRetailPrice, {
  readonly retail_price: string | null;
  readonly retail_price_currency: string | null;
  readonly retail_price_includes_vat: boolean | null;
  readonly retail_price_vat_rate: string | null;
  readonly retail_price_kind: string | null;
  readonly retail_price_provenance: string | null;
  readonly retail_price_valid_from: string | null;
  readonly retail_price_absent_reason: string | null;
}>>();

/** The unit cost of a report row — the generated row's own fields. */
export const TRANSFER_UNIT_COST: (row: TransferReportRow) => TransferUnitCost = (row) => row;

/** The retail price of a report row — the generated row's own fields. */
export const TRANSFER_RETAIL_PRICE: (row: TransferReportRow) => TransferRetailPrice = (row) => row;

/** Why `unit_cost` is null (ADR 6130), in the spec's order. Each has words in `why.ts`. */
export const UNIT_COST_ABSENT_REASONS = [
  'as_of_not_recorded_by_run', 'landed_cost_not_landed', 'landed_cost_not_in_force', 'cost_currency_not_reported',
] as const;

/** Why `retail_price` is null (ADR 6130), in the spec's order. Each has words in `why.ts`. */
export const RETAIL_PRICE_ABSENT_REASONS = [
  'as_of_not_recorded_by_run', 'retail_price_not_landed', 'retail_price_not_in_force', 'retail_price_currency_not_reported',
  'retail_price_vat_basis_not_reported', 'retail_price_kind_not_reported',
] as const;

/** reqs/07 R3's cost provenance; only `ACTUAL` is a measured cost. Production today is `STANDARD`. */
export const COST_PROVENANCES = ['ACTUAL', 'STANDARD', 'MODELLED', 'UNKNOWN'] as const;

/** The retail price's provenance; only `SOURCE` is a price a source system stated. */
export const PRICE_PROVENANCES = ['SOURCE', 'CALCULATED', 'UNKNOWN'] as const;

/** What the price is: `list` (the standing list price) or `ticket` (the ticket's, maybe marked down). */
export const RETAIL_PRICE_KINDS = ['list', 'ticket'] as const;

/**
 * The fallback for an API older than v0.59.0, which sends none of these fields: the value is absent
 * because the source has not sent it — an existing reason (`not_published`), never a zero.
 */
export const VALUE_NOT_SENT = 'not_published';
